/**
 * Dayjs configuration — the only place dayjs is imported and configured.
 * All date handling in `@pandas/core` (and every consumer) goes through
 * `./utils`; never import `dayjs` or call `new Date()`/`Date.now()` elsewhere.
 */

import type { Locale } from "@pandas/i18n-v2/locales";
import dayjs from "dayjs";
// Explicit `.js` extensions on every deep dayjs subpath: dayjs's package.json has no "exports" map, so bare specifiers resolve fine under bundlers but fail strict Node ESM resolution (Playwright's test loader), throwing `ERR_MODULE_NOT_FOUND`.
import el from "dayjs/locale/el.js";
import en from "dayjs/locale/en.js";
import fr from "dayjs/locale/fr.js";
import hr from "dayjs/locale/hr.js";
import ja from "dayjs/locale/ja.js";
import nl from "dayjs/locale/nl.js";
import sk from "dayjs/locale/sk.js";
import advancedFormat from "dayjs/plugin/advancedFormat.js";
import customParseFormat from "dayjs/plugin/customParseFormat.js";
import timezone from "dayjs/plugin/timezone.js";
import utc from "dayjs/plugin/utc.js";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

/** Date-format locales: one per compiled product-v2 language, so a new language without its dayjs locale fails to type-check. */
export type DateLocale = Locale;

const DATE_LOCALES: Record<DateLocale, ILocale> = { en, el, fr, nl, hr, sk, ja };

/** Set the active dayjs locale. */
export function setDateLocale(locale: DateLocale): void {
	dayjs.locale(DATE_LOCALES[locale]);
}

export { dayjs };
